import { CollapsibleFilters } from "../common/CollapsibleFilters";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, Star } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { ResponsiveTable } from "../common/ResponsiveTable";
import { MediaUploadModal } from "./MediaUploadModal";
import { MediaDetails, MediaPreview } from "./MediaDetails";
import {
  bulkMedia,
  mediaError,
  tagNames,
  type MediaCollection,
  type MediaFile,
  type MediaPage,
  type MediaUsage,
} from "./mediaApi";

interface MediaLibraryProps {
  selectionMode?: boolean;
  onSelect?: (
    media: Omit<MediaFile, "file_type"> & { file_type: "image" | "video" },
  ) => void;
  selectedMedia?: MediaFile | null;
  acceptedTypes?: ("image" | "video")[];
}
const initialFilters = {
  search: "",
  source: "",
  file_type: "",
  orientation: "",
  date_from: "",
  date_to: "",
  favorite: "",
  collection: "",
  ordering: "-uploaded_at",
};
export function MediaLibrary({
  selectionMode = false,
  onSelect,
  selectedMedia,
  acceptedTypes = ["image"],
}: MediaLibraryProps) {
  const [filters, setFilters] = useState(initialFilters);
  const [tags, setTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [upload, setUpload] = useState(false);
  const [details, setDetails] = useState<MediaFile | null>(null);
  const [selected, setSelected] = useState<MediaFile[]>([]);
  const [operation, setOperation] = useState<"tags" | "collection" | "delete">(
    "tags",
  );
  const [bulkTags, setBulkTags] = useState("");
  const [collectionId, setCollectionId] = useState<number | null>(null);
  const [removeCollection, setRemoveCollection] = useState(false);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [newCollection, setNewCollection] = useState("");
  const [manageCollections, setManageCollections] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(false);
  const cache = useQueryClient();
  const filter = (key: keyof typeof filters, value: string) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPage(1);
  };
  const library = useQuery({
    queryKey: [
      "combined-media",
      filters,
      tags,
      page,
      selectionMode,
      acceptedTypes.join(","),
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ ...filters, page: String(page) });
      tags.forEach((tag) => params.append("tags", tag));
      if (selectionMode && acceptedTypes.length === 1)
        params.set("file_type", acceptedTypes[0]);
      return (
        await api.get<MediaPage>(`/api/marketing/combined-media/?${params}`)
      ).data;
    },
  });
  const collections = useQuery({
    queryKey: ["media-collections"],
    queryFn: async () =>
      (await api.get<MediaCollection[]>("/api/marketing/media-collections/"))
        .data,
  });
  const refs = useQuery({
    queryKey: ["media-bulk-references", selected.map((row) => row.id)],
    enabled: review && operation === "delete",
    queryFn: async () =>
      Promise.all(
        selected.map(async (row) => ({
          id: row.id,
          ...(
            await api.get<MediaUsage>(
              `/api/marketing/combined-media/${row.id}/usage/`,
            )
          ).data,
        })),
      ),
  });
  const rows = library.data?.results || [];
  const total = library.data?.count || 0;
  useEffect(() => {
    if (library.data && page > 1 && !library.data.results.length)
      setPage(Math.max(1, Math.ceil(library.data.count / 20)));
  }, [library.data, page]);
  const availableTags = [
    ...new Set([...(library.data?.facets?.tags || []), ...tags]),
  ];
  const hasFilters =
    tags.length > 0 ||
    Object.entries(filters).some(
      ([key, value]) =>
        value !== initialFilters[key as keyof typeof initialFilters],
    );
  const refresh = () =>
    cache.invalidateQueries({ queryKey: ["combined-media"] });
  const select = (row: MediaFile) =>
    setSelected((previous) =>
      previous.some((item) => item.id === row.id)
        ? previous.filter((item) => item.id !== row.id)
        : [...previous, row],
    );
  const favorite = async (row: MediaFile) => {
    setError("");
    try {
      await bulkMedia({
        ids: [row.id],
        operation: "favorite",
        favorite: !row.favorite,
      });
      await refresh();
    } catch (cause) {
      setError(mediaError(cause));
    }
  };
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await bulkMedia({
        ids: selected.map((row) => row.id),
        operation,
        tags: tagNames(bulkTags),
        collection: collectionId,
        remove: removeCollection,
      });
      setNotice(
        `${result.changed.length} assets updated.${result.skipped.length ? ` ${result.skipped.length} skipped: ${result.skipped.map((item) => item.reason).join(" ")}` : ""}`,
      );
      setSelected((previous) =>
        previous.filter((row) => !result.changed.includes(String(row.id))),
      );
      setReview(false);
      await refresh();
    } catch (cause) {
      setError(mediaError(cause));
    }
    setBusy(false);
  };
  const createCollection = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/marketing/media-collections/", {
        name: newCollection,
      });
      setNewCollection("");
      await cache.invalidateQueries({ queryKey: ["media-collections"] });
    } catch (cause) {
      setError(mediaError(cause));
    }
    setBusy(false);
  };
  const canPick = (row: MediaFile) =>
    row.file_type !== "other" && acceptedTypes.includes(row.file_type);
  const pick = (row: MediaFile) => {
    if (row.file_type !== "other" && canPick(row))
      onSelect?.({ ...row, file_type: row.file_type });
  };
  const actions = (row: MediaFile) => (
    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
      {selectionMode ? (
        <Button
          size="sm"
          disabled={!canPick(row)}
          aria-label={`Select ${row.name}`}
          aria-pressed={selectedMedia?.id === row.id}
          onClick={() => pick(row)}
        >
          {selectedMedia?.id === row.id ? "Selected" : "Choose"}
        </Button>
      ) : (
        <Checkbox
          label="Select"
          checked={selected.some((item) => item.id === row.id)}
          disabled={
            selected.length >= 100 &&
            !selected.some((item) => item.id === row.id)
          }
          onChange={() => select(row)}
        />
      )}
      <Button size="sm" variant="plain" onClick={() => setDetails(row)}>
        Details
      </Button>
      <Button
        size="sm"
        variant="plain"
        aria-label={
          row.favorite ? `Unfavorite ${row.name}` : `Favorite ${row.name}`
        }
        onClick={() => favorite(row)}
      >
        <Star weight={row.favorite ? "fill" : "regular"} />
      </Button>
    </Stack>
  );
  const pages = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
    >
      <Button
        variant="outlined"
        disabled={page === 1 || library.isFetching}
        onClick={() => setPage((previous) => previous - 1)}
      >
        Previous
      </Button>
      <Typography level="body-sm">
        Page {page} of {Math.max(1, Math.ceil(total / 20))}
      </Typography>
      <Button
        variant="outlined"
        disabled={page * 20 >= total || library.isFetching}
        onClick={() => setPage((previous) => previous + 1)}
      >
        Next
      </Button>
    </Stack>
  );
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack
        direction={selectionMode ? "row" : { xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
      >
        <Box>
          {selectionMode ? (
            <Typography level="title-sm">Your media library</Typography>
          ) : (
            <>
              <Typography level="h4">Your campaign assets</Typography>
              <Typography level="body-sm">
                Marketing uploads and production photos tagged “marketing”.
              </Typography>
            </>
          )}
        </Box>
        <Button
          sx={{ flexShrink: 0, alignSelf: "flex-start" }}
          onClick={() => setUpload(true)}
        >
          {selectionMode &&
          acceptedTypes.length === 1 &&
          acceptedTypes[0] === "image"
            ? "Upload images"
            : "Upload media"}
        </Button>
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      <CollapsibleFilters collapsible={!selectionMode}><Input
        aria-label="Search media"
        placeholder="Search name, description or filename"
        value={filters.search}
        onChange={(event) => filter("search", event.target.value)}
      />
      <Box
        sx={{
          display:
            selectionMode && !advancedFilters
              ? { xs: "none", sm: "grid" }
              : "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
          gap: 1,
        }}
      >
        <FormControl>
          <FormLabel>Source</FormLabel>
          <Select
            value={filters.source}
            onChange={(_, v) => filter("source", v || "")}
          >
            <Option value="">All sources</Option>
            <Option value="marketing">Marketing</Option>
            <Option value="production">Production</Option>
          </Select>
        </FormControl>
        {(!selectionMode || acceptedTypes.length !== 1) && (
          <FormControl>
            <FormLabel>Type</FormLabel>
            <Select
              disabled={selectionMode && acceptedTypes.length === 1}
              value={
                selectionMode && acceptedTypes.length === 1
                  ? acceptedTypes[0]
                  : filters.file_type
              }
              onChange={(_, v) => filter("file_type", v || "")}
            >
              <Option value="">All types</Option>
              <Option value="image">Images</Option>
              <Option value="video">Videos</Option>
            </Select>
          </FormControl>
        )}
        {advancedFilters && (
          <FormControl>
            <FormLabel>Orientation</FormLabel>
            <Select
              value={filters.orientation}
              onChange={(_, v) => filter("orientation", v || "")}
            >
              <Option value="">Any orientation</Option>
              {["landscape", "portrait", "square", "unknown"].map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl>
          <FormLabel>Sort</FormLabel>
          <Select
            value={filters.ordering}
            onChange={(_, v) => filter("ordering", v || "-uploaded_at")}
          >
            <Option value="-uploaded_at">Newest first</Option>
            <Option value="uploaded_at">Oldest first</Option>
            <Option value="name">Name A–Z</Option>
            <Option value="-file_size">Largest first</Option>
          </Select>
        </FormControl>
        {advancedFilters && (
          <FormControl>
            <FormLabel>Uploaded from</FormLabel>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(event) => filter("date_from", event.target.value)}
            />
          </FormControl>
        )}
        {advancedFilters && (
          <FormControl>
            <FormLabel>Uploaded through</FormLabel>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(event) => filter("date_to", event.target.value)}
            />
          </FormControl>
        )}
        {(!selectionMode || advancedFilters) && (
          <FormControl>
            <FormLabel>Collection</FormLabel>
            <Select
              value={filters.collection}
              onChange={(_, v) => filter("collection", v || "")}
            >
              <Option value="">All collections</Option>
              {collections.data?.map((c) => (
                <Option key={c.id} value={String(c.id)}>
                  {c.name}
                </Option>
              ))}
            </Select>
          </FormControl>
        )}
        {advancedFilters && (
          <FormControl>
            <FormLabel>Favorites</FormLabel>
            <Select
              value={filters.favorite}
              onChange={(_, v) => filter("favorite", v || "")}
            >
              <Option value="">All assets</Option>
              <Option value="true">Favorites only</Option>
            </Select>
          </FormControl>
        )}
      </Box>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
        <Button
          variant="plain"
          size="sm"
          onClick={() => setAdvancedFilters((value) => !value)}
        >
          {advancedFilters ? "Hide extra filters" : "More filters"}
          {[
            filters.orientation,
            filters.date_from,
            filters.date_to,
            filters.favorite,
            selectionMode ? filters.collection : "",
          ].filter(Boolean).length
            ? " · Active"
            : ""}
        </Button>
        {!selectionMode && (
          <Button
            variant="plain"
            size="sm"
            onClick={() => setManageCollections(true)}
          >
            Manage collections
          </Button>
        )}
      </Stack>
      {collections.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => collections.refetch()}>Retry</Button>
          }
        >
          Collections could not be loaded.
        </Alert>
      )}
      {(!selectionMode || advancedFilters || hasFilters) && (
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          {(!selectionMode || advancedFilters || tags.length > 0) &&
            availableTags.map((tag) => (
              <Chip
                key={tag}
                variant={tags.includes(tag) ? "solid" : "soft"}
                onClick={() => {
                  setTags((previous) =>
                    previous.includes(tag)
                      ? previous.filter((value) => value !== tag)
                      : [...previous, tag],
                  );
                  setPage(1);
                }}
              >
                {tag}
              </Chip>
            ))}
          <Button
            size="sm"
            variant="plain"
            onClick={() => {
              setFilters(initialFilters);
              setTags([]);
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        </Stack>
      )}
</CollapsibleFilters>
      {!!selected.length && !selectionMode && (
        <Card>
          <Typography level="title-md">
            {selected.length} selected across pages
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            useFlexGap
            flexWrap="wrap"
            spacing={1}
          >
            <Select
              value={operation}
              onChange={(_, v) => setOperation(v || "tags")}
            >
              <Option value="tags">Add tags</Option>
              <Option value="collection">Update collection</Option>
              <Option value="delete">Delete eligible marketing assets</Option>
            </Select>
            {operation === "tags" && (
              <Input
                aria-label="Bulk tags"
                placeholder="Tags separated by commas"
                value={bulkTags}
                onChange={(event) => setBulkTags(event.target.value)}
              />
            )}
            {operation === "collection" && (
              <>
                <Select
                  placeholder="Choose collection"
                  value={collectionId}
                  onChange={(_, v) => setCollectionId(v)}
                >
                  {collections.data?.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
                <Checkbox
                  label="Remove from collection"
                  checked={removeCollection}
                  onChange={(event) =>
                    setRemoveCollection(event.target.checked)
                  }
                />
              </>
            )}
            <Button
              disabled={
                operation === "tags"
                  ? !tagNames(bulkTags).length
                  : operation === "collection" && !collectionId
              }
              onClick={() => setReview(true)}
            >
              Review action
            </Button>
            <Button variant="plain" onClick={() => setSelected([])}>
              Clear selection
            </Button>
          </Stack>
        </Card>
      )}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography level="title-sm">
          {library.isPending
            ? "Loading assets…"
            : library.isError
              ? "Library unavailable"
              : `${total} assets${library.isFetching ? " · Updating…" : ""}`}
        </Typography>
        <Select
          aria-label="View layout"
          value={view}
          onChange={(_, value) => setView(value || "grid")}
        >
          <Option value="grid">Grid view</Option>
          <Option value="list">List view</Option>
        </Select>
      </Stack>
      {library.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => library.refetch()}>Retry</Button>
          }
        >
          Media could not be loaded. {mediaError(library.error)}
        </Alert>
      )}
      {!library.isError && !library.isPending && !rows.length && (
        <Card>
          <Typography level="title-md">
            No assets match these filters
          </Typography>
          <Typography>Clear filters or upload media to get started.</Typography>
        </Card>
      )}
      {view === "grid" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
            gap: 2,
          }}
        >
          {rows.map((row) =>
            selectionMode ? (
              <Card
                key={row.id}
                variant="outlined"
                color={selectedMedia?.id === row.id ? "primary" : "neutral"}
                sx={{
                  minWidth: 0,
                  p: 0,
                  overflow: "hidden",
                  outline:
                    selectedMedia?.id === row.id
                      ? "2px solid var(--joy-palette-primary-500)"
                      : undefined,
                  outlineOffset: -2,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={`Select ${row.name}`}
                  aria-pressed={selectedMedia?.id === row.id}
                  disabled={!canPick(row)}
                  onClick={() => pick(row)}
                  sx={{
                    p: 1.5,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    color: "inherit",
                    textAlign: "left",
                    border: 0,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "background.level1" },
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: "primary.500",
                      outlineOffset: -3,
                    },
                  }}
                >
                  <MediaPreview media={row} />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: "100%", minWidth: 0 }}
                  >
                    <Typography
                      level="title-sm"
                      title={row.name}
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflowWrap: "anywhere",
                        minHeight: "2.8em",
                      }}
                    >
                      {row.name}
                    </Typography>
                    {selectedMedia?.id === row.id && (
                      <CheckCircle
                        size={24}
                        weight="fill"
                        aria-hidden
                        style={{ flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                  <Typography level="body-xs">
                    {row.source_app_display ||
                      (row.source === "production" ? "Job photo" : "Marketing")}
                    {row.dimensions?.width
                      ? ` · ${row.dimensions.width} × ${row.dimensions.height}`
                      : ""}
                  </Typography>
                </Box>
              </Card>
            ) : (
              <Card key={row.id} sx={{ minWidth: 0 }}>
                <MediaPreview media={row} />
                <Typography level="title-md" sx={{ overflowWrap: "anywhere" }}>
                  {row.name}
                </Typography>
                <Typography level="body-xs">
                  {row.source_app_display} · {row.file_type} ·{" "}
                  {new Date(row.uploaded_at).toLocaleDateString()}
                </Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5}>
                  {row.tags?.map((tag) => (
                    <Chip key={tag} size="sm">
                      {tag}
                    </Chip>
                  ))}
                </Stack>
                {actions(row)}
              </Card>
            ),
          )}
        </Box>
      ) : (
        <ResponsiveTable
          rows={rows}
          getRowKey={(row) => row.id}
          columns={[
            {
              key: "name",
              header: "Asset",
              mobileVariant: "header",
              render: (row) => (
                <Typography sx={{ overflowWrap: "anywhere" }}>
                  {row.name}
                </Typography>
              ),
            },
            {
              key: "source",
              header: "Source",
              render: (row) => row.source_app_display,
            },
            { key: "type", header: "Type", render: (row) => row.file_type },
            {
              key: "date",
              header: "Uploaded",
              render: (row) => new Date(row.uploaded_at).toLocaleDateString(),
            },
          ]}
          rowActions={actions}
        />
      )}
      {!library.isError && pages}

      <Modal
        open={manageCollections}
        onClose={() => {
          if (!busy) setManageCollections(false);
        }}
      >
        <ModalDialog
          sx={{
            width: 620,
            maxWidth: "94vw",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">Collections</Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {!selectionMode && (
            <Card variant="soft">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Input
                  aria-label="New collection name"
                  placeholder="New collection name"
                  value={newCollection}
                  onChange={(event) => setNewCollection(event.target.value)}
                />
                <Button
                  variant="outlined"
                  loading={busy}
                  disabled={!newCollection.trim()}
                  onClick={createCollection}
                >
                  Create collection
                </Button>
              </Stack>
              <Typography level="body-xs">
                Collections and favorites are shared with your organization.
                Production originals stay in their jobs.
              </Typography>
            </Card>
          )}

          {collections.data?.map((collection) => (
            <Stack
              direction="row"
              key={collection.id}
              spacing={1}
              alignItems="center"
            >
              <Typography sx={{ flex: 1, overflowWrap: "anywhere" }}>
                {collection.name}
              </Typography>
              <Button
                variant="plain"
                disabled={busy}
                onClick={async () => {
                  const name = window.prompt(
                    "Collection name",
                    collection.name,
                  );
                  if (!name?.trim() || name === collection.name) return;
                  setBusy(true);
                  setError("");
                  try {
                    await api.patch(
                      `/api/marketing/media-collections/${collection.id}/`,
                      { name },
                    );
                    await cache.invalidateQueries({
                      queryKey: ["media-collections"],
                    });
                  } catch (cause) {
                    setError(mediaError(cause));
                  }
                  setBusy(false);
                }}
              >
                Rename
              </Button>
              <Button
                variant="plain"
                color="danger"
                disabled={busy}
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Remove collection “${collection.name}”? Assets will stay in the library.`,
                    )
                  )
                    return;
                  setBusy(true);
                  setError("");
                  try {
                    await api.delete(
                      `/api/marketing/media-collections/${collection.id}/`,
                    );
                    if (filters.collection === String(collection.id))
                      filter("collection", "");
                    if (collectionId === collection.id) setCollectionId(null);
                    await cache.invalidateQueries({
                      queryKey: ["media-collections"],
                    });
                    await refresh();
                  } catch (cause) {
                    setError(mediaError(cause));
                  }
                  setBusy(false);
                }}
              >
                Remove
              </Button>
            </Stack>
          ))}
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => setManageCollections(false)}
          >
            Close
          </Button>
        </ModalDialog>
      </Modal>
      <MediaUploadModal
        open={upload}
        onClose={() => setUpload(false)}
        acceptedTypes={selectionMode ? acceptedTypes : undefined}
      />
      {details && (
        <MediaDetails
          key={details.id}
          media={details}
          onClose={() => setDetails(null)}
          onUpdated={refresh}
        />
      )}
      <Modal
        open={review}
        onClose={() => {
          if (!busy) setReview(false);
        }}
      >
        <ModalDialog
          sx={{
            width: 620,
            maxWidth: "94vw",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">Review bulk action</Typography>
          {error && <Alert color="danger">{error}</Alert>}
          <Typography>
            {operation === "tags"
              ? `Add tags: ${tagNames(bulkTags).join(", ")}`
              : operation === "collection"
                ? `${removeCollection ? "Remove from" : "Add to"} collection: ${collections.data?.find((c) => c.id === collectionId)?.name}`
                : "Delete eligible marketing assets. Production originals and referenced assets will be skipped."}
          </Typography>
          {selected.map((row) => (
            <Typography key={row.id} level="body-sm">
              {row.name} · {row.source}
            </Typography>
          ))}
          {operation === "delete" && (
            <>
              {refs.isPending && <Typography>Checking references…</Typography>}
              {refs.isError && (
                <Alert
                  color="danger"
                  endDecorator={
                    <Button onClick={() => refs.refetch()}>Retry</Button>
                  }
                >
                  Could not check references.
                </Alert>
              )}
              {refs.data?.map((row) => (
                <Typography key={row.id} level="body-sm">
                  {row.id}:{" "}
                  {row.can_delete
                    ? "Eligible for deletion"
                    : `Will be kept (${row.references.length} known references or production original)`}
                </Typography>
              ))}
            </>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="plain"
              disabled={busy}
              onClick={() => setReview(false)}
            >
              Cancel
            </Button>
            <Button
              color={operation === "delete" ? "danger" : "primary"}
              loading={busy}
              disabled={operation === "delete" && (!refs.data || refs.isError)}
              onClick={run}
            >
              Apply to {selected.length} assets
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
